import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/client';
import type { EmployeeListItem } from '../services/types';
import { BilingualText } from '../components/BilingualText';
import { BrandHeader } from '../components/BrandHeader';
import { dict } from '../i18n/dictionary';

function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/\s+/g, '')
    .normalize('NFKC');
}

export function SelectName() {
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api
      .listEmployees()
      .then((list) => setEmployees(list))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main className="page">
        <BrandHeader />
        <BilingualText value={dict.loading} />
      </main>
    );
  }
  if (error) {
    return (
      <main className="page">
        <BrandHeader />
        <p className="error">
          <BilingualText value={dict.networkError} />
        </p>
        <p className="error-detail">{error}</p>
      </main>
    );
  }
  if (employees.length === 0) {
    return (
      <main className="page">
        <BrandHeader />
        <BilingualText value={dict.noEmployees} />
      </main>
    );
  }

  const q = normalize(query);
  const filtered =
    q === '' ? employees : employees.filter((e) => {
      const hay = normalize(`${e.name_ja} ${e.name_vi ?? ''}`);
      return hay.includes(q);
    });

  return (
    <main className="page page-select">
      <BrandHeader />
      <header className="page-header">
        <h1>
          <BilingualText value={dict.appTitle} />
        </h1>
        <p className="subtitle">
          <BilingualText value={dict.selectName} />
        </p>
      </header>
      <div className="search-wrap">
        <input
          type="search"
          className="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`${dict.searchPlaceholder.ja} / ${dict.searchPlaceholder.vi}`}
          aria-label={dict.searchPlaceholder.ja}
        />
      </div>
      {filtered.length === 0 ? (
        <p className="muted" style={{ textAlign: 'center', padding: '24px' }}>
          <BilingualText value={dict.noMatch} />
        </p>
      ) : (
        <ul className="employee-list">
          {filtered.map((e) => (
            <li key={e.id}>
              <button
                className="employee-card"
                onClick={() => navigate(`/pin/${e.id}`)}
                aria-label={`${e.name_ja} ${e.name_vi ?? ''}`}
              >
                <div className="avatar" aria-hidden>
                  {e.name_ja.charAt(0)}
                </div>
                <div className="employee-names">
                  <div className="name-ja">{e.name_ja}</div>
                  {e.name_vi && <div className="name-vi">{e.name_vi}</div>}
                </div>
                <div className="chevron" aria-hidden>
                  ›
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
